import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import useLanguageStore from '@stores/languageStore';
import useCartStore, { SERVICE_TYPES } from '@stores/cartStore';
import { useAppContext } from '@contexts/AppContext';
import { submitOrder } from '@services/api';
import SEOHead from '../../components/SEOHead';

const OrderPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguageStore();
  const isRtl = language === 'he';
  const navigate = useNavigate();
  const { showSuccess, showError, setLoading } = useAppContext();

  const { items, clearCart } = useCartStore();

  const [formData, setFormData] = useState({
    customerName: '',
    familyName: '',
    idNumber: '',
    street: '',
    city: '',
    homePhone: '',
    tombstoneDesignPhone: '',
    cemetery: '',
    area: '',
    block: '',
    row: '',
    grave: '',
    stoneType: '',
  });

  const [errors, setErrors] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = t('order.form.errors.nameRequired');
    }

    if (!formData.familyName.trim()) {
      newErrors.familyName = t('order.form.errors.familyNameRequired');
    }

    if (!formData.idNumber.trim()) {
      newErrors.idNumber = t('order.form.errors.idNumberRequired');
    }

    if (!formData.street.trim()) {
      newErrors.street = t('order.form.errors.streetRequired');
    }

    if (!formData.city.trim()) {
      newErrors.city = t('order.form.errors.cityRequired');
    }

    if (!formData.homePhone.trim()) {
      newErrors.homePhone = t('order.form.errors.homePhoneRequired');
    }

    if (!formData.tombstoneDesignPhone.trim()) {
      newErrors.tombstoneDesignPhone = t('order.form.errors.tombstoneDesignPhoneRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (items.length === 0) {
      showError(t('order.errors.emptyCart'));
      navigate('/cart');
      return;
    }

    // Open confirmation dialog (WCAG 3.3.4 - Error Prevention)
    setConfirmDialogOpen(true);
  };

  const handleConfirmOrder = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);

    try {
      const orderData = {
        customer: formData,
        items: items.map((item) => ({
          serviceType: item.serviceType,
          data: item.data,
          quantity: item.quantity,
        })),
        orderDate: new Date().toISOString(),
        language: language,
      };

      const result = await submitOrder(orderData);

      if (result.success) {
        showSuccess(t('order.success.message'));
        clearCart();
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        showError(t('order.errors.submissionFailed'));
      }
    } catch (error) {
      console.error('Order submission error:', error);
      showError(t('order.errors.submissionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getServiceName = (serviceType) => {
    switch (serviceType) {
      case SERVICE_TYPES.OBITUARY:
        return t('home.services.obituary.title');
      case SERVICE_TYPES.TOMBSTONE:
        return t('home.services.tombstone.title');
      case SERVICE_TYPES.SCHEDULE_RABBI:
        return t('home.services.scheduleRabi.title');
      case SERVICE_TYPES.REFURBISH:
        return t('home.services.refurbish.title');
      default:
        return serviceType;
    }
  };

  if (items.length === 0) {
    return (
      <div
        className={`min-h-[calc(100vh-64px)] bg-stone-50 py-8 sm:py-16 max-w-[100vw] overflow-x-hidden ${
          isRtl ? 'rtl' : 'ltr'
        }`}
      >
        <Container maxWidth="lg" className="px-4 sm:px-8">
          <Paper className="p-12 text-center bg-white shadow-lg" elevation={3}>
            <Typography variant="h4" component="h1" className="mb-4 font-bold text-stone-800">
              {t('order.empty.title')}
            </Typography>
            <Typography variant="body1" className="mb-8 text-stone-600">
              {t('order.empty.description')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/cart')}
              className="px-8 py-3 normal-case font-semibold bg-stone-700 hover:bg-stone-600"
            >
              {t('order.empty.backToCart')}
            </Button>
          </Paper>
        </Container>
      </div>
    );
  }

  return (
    <div
      className={`min-h-[calc(100vh-64px)] bg-stone-50 py-8 sm:py-16 max-w-[100vw] overflow-x-hidden ${
        isRtl ? 'rtl' : 'ltr'
      }`}
    >
      <SEOHead title={t('order.title')} />
      <Container maxWidth="lg" className="px-4 sm:px-8">
        <div className="mb-8">
          <Typography variant="h3" component="h1" className="mb-2 font-bold text-stone-800">
            {t('order.title')}
          </Typography>
          <Typography variant="body1" className="text-stone-600">
            {t('order.subtitle')}
          </Typography>
        </div>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Paper className="p-8 bg-white shadow-lg" elevation={3}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={4}>
                  {/* Customer Information Section */}
                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      component="h3"
                      className="mb-4 font-semibold text-stone-700 border-b border-stone-200 pb-2"
                    >
                      {t('order.form.title')}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="customerName"
                      fullWidth
                      label={t('order.form.customerName')}
                      value={formData.customerName}
                      onChange={handleChange('customerName')}
                      error={!!errors.customerName}
                      helperText={errors.customerName}
                      required
                      aria-required="true"
                      aria-invalid={!!errors.customerName}
                      aria-describedby={errors.customerName ? 'customerName-error' : undefined}
                      inputProps={{
                        dir: isRtl ? 'rtl' : 'ltr',
                        'aria-describedby': errors.customerName ? 'customerName-error' : undefined,
                      }}
                      FormHelperTextProps={{
                        id: errors.customerName ? 'customerName-error' : undefined,
                        role: errors.customerName ? 'alert' : undefined,
                      }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="familyName"
                      fullWidth
                      label={t('order.form.familyName')}
                      value={formData.familyName}
                      onChange={handleChange('familyName')}
                      error={!!errors.familyName}
                      helperText={errors.familyName}
                      required
                      aria-required="true"
                      aria-invalid={!!errors.familyName}
                      aria-describedby={errors.familyName ? 'familyName-error' : undefined}
                      inputProps={{
                        dir: isRtl ? 'rtl' : 'ltr',
                        'aria-describedby': errors.familyName ? 'familyName-error' : undefined,
                      }}
                      FormHelperTextProps={{
                        id: errors.familyName ? 'familyName-error' : undefined,
                        role: errors.familyName ? 'alert' : undefined,
                      }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="idNumber"
                      fullWidth
                      label={t('order.form.idNumber')}
                      value={formData.idNumber}
                      onChange={handleChange('idNumber')}
                      error={!!errors.idNumber}
                      helperText={errors.idNumber}
                      required
                      aria-required="true"
                      aria-invalid={!!errors.idNumber}
                      aria-describedby={errors.idNumber ? 'idNumber-error' : undefined}
                      inputProps={{
                        dir: isRtl ? 'rtl' : 'ltr',
                        'aria-describedby': errors.idNumber ? 'idNumber-error' : undefined,
                      }}
                      FormHelperTextProps={{
                        id: errors.idNumber ? 'idNumber-error' : undefined,
                        role: errors.idNumber ? 'alert' : undefined,
                      }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="city"
                      fullWidth
                      label={t('order.form.city')}
                      value={formData.city}
                      onChange={handleChange('city')}
                      error={!!errors.city}
                      helperText={errors.city}
                      required
                      aria-required="true"
                      aria-invalid={!!errors.city}
                      aria-describedby={errors.city ? 'city-error' : undefined}
                      inputProps={{
                        dir: isRtl ? 'rtl' : 'ltr',
                        'aria-describedby': errors.city ? 'city-error' : undefined,
                      }}
                      FormHelperTextProps={{
                        id: errors.city ? 'city-error' : undefined,
                        role: errors.city ? 'alert' : undefined,
                      }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      id="street"
                      fullWidth
                      label={t('order.form.street')}
                      value={formData.street}
                      onChange={handleChange('street')}
                      error={!!errors.street}
                      helperText={errors.street}
                      required
                      aria-required="true"
                      aria-invalid={!!errors.street}
                      aria-describedby={errors.street ? 'street-error' : undefined}
                      inputProps={{
                        dir: isRtl ? 'rtl' : 'ltr',
                        'aria-describedby': errors.street ? 'street-error' : undefined,
                      }}
                      FormHelperTextProps={{
                        id: errors.street ? 'street-error' : undefined,
                        role: errors.street ? 'alert' : undefined,
                      }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="homePhone"
                      fullWidth
                      label={t('order.form.homePhone')}
                      type="tel"
                      value={formData.homePhone}
                      onChange={handleChange('homePhone')}
                      error={!!errors.homePhone}
                      helperText={errors.homePhone}
                      required
                      aria-required="true"
                      aria-invalid={!!errors.homePhone}
                      aria-describedby={errors.homePhone ? 'homePhone-error' : undefined}
                      inputProps={{
                        dir: isRtl ? 'rtl' : 'ltr',
                        'aria-describedby': errors.homePhone ? 'homePhone-error' : undefined,
                      }}
                      FormHelperTextProps={{
                        id: errors.homePhone ? 'homePhone-error' : undefined,
                        role: errors.homePhone ? 'alert' : undefined,
                      }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      id="tombstoneDesignPhone"
                      fullWidth
                      label={t('order.form.tombstoneDesignPhone')}
                      type="tel"
                      value={formData.tombstoneDesignPhone}
                      onChange={handleChange('tombstoneDesignPhone')}
                      error={!!errors.tombstoneDesignPhone}
                      helperText={errors.tombstoneDesignPhone}
                      required
                      aria-required="true"
                      aria-invalid={!!errors.tombstoneDesignPhone}
                      aria-describedby={
                        errors.tombstoneDesignPhone ? 'tombstoneDesignPhone-error' : undefined
                      }
                      inputProps={{
                        dir: isRtl ? 'rtl' : 'ltr',
                        'aria-describedby': errors.tombstoneDesignPhone
                          ? 'tombstoneDesignPhone-error'
                          : undefined,
                      }}
                      FormHelperTextProps={{
                        id: errors.tombstoneDesignPhone ? 'tombstoneDesignPhone-error' : undefined,
                        role: errors.tombstoneDesignPhone ? 'alert' : undefined,
                      }}
                      variant="outlined"
                    />
                  </Grid>

                  {/* Tombstone Location Section */}
                  <Grid item xs={12}>
                    <Typography
                      variant="h6"
                      component="h3"
                      className="mb-4 mt-4 font-semibold text-stone-700 border-b border-stone-200 pb-2"
                    >
                      {t('order.form.tombstoneLocation')}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('order.form.cemetery')}
                      value={formData.cemetery}
                      onChange={handleChange('cemetery')}
                      inputProps={{ dir: isRtl ? 'rtl' : 'ltr' }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('order.form.area')}
                      value={formData.area}
                      onChange={handleChange('area')}
                      inputProps={{ dir: isRtl ? 'rtl' : 'ltr' }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label={t('order.form.block')}
                      value={formData.block}
                      onChange={handleChange('block')}
                      inputProps={{ dir: isRtl ? 'rtl' : 'ltr' }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label={t('order.form.row')}
                      value={formData.row}
                      onChange={handleChange('row')}
                      inputProps={{ dir: isRtl ? 'rtl' : 'ltr' }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label={t('order.form.grave')}
                      value={formData.grave}
                      onChange={handleChange('grave')}
                      inputProps={{ dir: isRtl ? 'rtl' : 'ltr' }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('order.form.stoneType')}
                      value={formData.stoneType}
                      onChange={handleChange('stoneType')}
                      inputProps={{ dir: isRtl ? 'rtl' : 'ltr' }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      className="py-3 normal-case font-semibold bg-stone-700 hover:bg-stone-600"
                    >
                      {t('order.form.submit')}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper className="p-6 bg-white shadow-lg sticky top-24" elevation={3}>
              <Typography variant="h5" component="h2" className="mb-4 font-bold text-stone-800">
                {t('order.summary.title')}
              </Typography>
              <Box className="mb-4 space-y-3">
                {items.map((item) => (
                  <Box key={item.id} className="pb-3 border-b border-stone-200 last:border-0">
                    <Typography variant="body1" className="font-semibold text-stone-800">
                      {getServiceName(item.serviceType)}
                    </Typography>
                    <Typography variant="body2" className="text-stone-600">
                      {t('order.summary.quantity')}: {item.quantity}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography variant="body1" className="font-semibold text-stone-800">
                {t('order.summary.totalItems', {
                  count: items.reduce((sum, item) => sum + item.quantity, 0),
                })}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Order Confirmation Dialog - WCAG 3.3.4 Error Prevention */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="order-confirm-title"
        aria-describedby="order-confirm-description"
      >
        <DialogTitle id="order-confirm-title">{t('order.confirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="order-confirm-description" component="div">
            <Typography variant="body1" className="mb-4">
              {t('order.confirm.message')}
            </Typography>
            <Box className="mt-4 p-4 bg-stone-50 rounded">
              <Typography variant="body2" component="p" className="font-semibold mb-2">
                {t('order.confirm.customerInfo')}
              </Typography>
              <Typography variant="body2" className="mb-1">
                <strong>{t('order.form.customerName')}:</strong> {formData.customerName}
              </Typography>
              <Typography variant="body2" className="mb-1">
                <strong>{t('order.form.familyName')}:</strong> {formData.familyName}
              </Typography>
              <Typography variant="body2" className="mb-1">
                <strong>{t('order.form.homePhone')}:</strong> {formData.homePhone}
              </Typography>
              <Typography variant="body2" component="p" className="font-semibold mt-4 mb-2">
                {t('order.summary.title')}
              </Typography>
              {items.map((item) => (
                <Typography key={item.id} variant="body2" className="mb-1">
                  {getServiceName(item.serviceType)} - {t('order.summary.quantity')}:{' '}
                  {item.quantity}
                </Typography>
              ))}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="secondary">
            {t('order.confirm.cancel')}
          </Button>
          <Button onClick={handleConfirmOrder} variant="contained" color="primary" autoFocus>
            {t('order.confirm.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrderPage;
